import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { getAccessToken } from '../../../auth';
import { envConfig } from '../../../config';
import Button from '../../../lib/button/Button';
import { popupNotification } from '../../../popup-notification';
import { EditorService } from '../../../services/editor';
import UpgradeBanner from '../UpgradeBanner';
import { useWorkspaceStore } from '../../../store/workspace';
import {
	resolveActionDescriptorNameById,
	toSentenceCase
} from '@elyx-code/project-logic-tree';
import { InfrastructureResourceType } from '@elyx-code/definitions';
import short from 'short-uuid';
import { getFunctionNameFromArn } from '../../../utils';
import HTTPEndpointIcon from '../../../assets/http-endpoint.svg';
import CronJobIcon from '../../../assets/cron-job.png';
import ExecutionIcon from '../../../assets/execution.svg';
import TerminalIcon from '../../../assets/terminal.svg';

interface ICloudLogsViewerProps {
	onClose: () => void;
	project: EditorService;
	projectId: string;
	functionName?: string;
	logGroupName?: string;
	hideHeader?: boolean;
}

interface ILogStream {
	logStreamName: string;
	creationTime: number;
	firstEventTimestamp: number;
	lastEventTimestamp: number;
	lastIngestionTime: number;
	uploadSequenceToken: string;
	arn: string;
}

interface ILogEvent {
	timestamp: number;
	message: string;
	ingestionTime: number;
}

const mockStreams: ILogStream[] = [
	{
		logStreamName: '2026/05/26/[$LATEST]abc123fake',
		creationTime: Date.parse('2026-05-26T18:00:00.000Z'),
		firstEventTimestamp: Date.parse('2026-05-26T18:15:00.000Z'),
		lastEventTimestamp: Date.parse('2026-05-26T18:15:10.000Z'),
		lastIngestionTime: Date.parse('2026-05-26T18:15:15.000Z'),
		uploadSequenceToken: 'seq-token-1',
		arn: 'arn:aws:logs:us-east-1:123456789012:log-group:/aws/lambda/fake:log-stream:2026/05/26/[$LATEST]abc123fake'
	},
	{
		logStreamName: '2026/05/26/[$LATEST]xyz789fake',
		creationTime: Date.parse('2026-05-26T17:00:00.000Z'),
		firstEventTimestamp: Date.parse('2026-05-26T17:15:00.000Z'),
		lastEventTimestamp: Date.parse('2026-05-26T17:15:05.000Z'),
		lastIngestionTime: Date.parse('2026-05-26T17:15:10.000Z'),
		uploadSequenceToken: 'seq-token-2',
		arn: 'arn:aws:logs:us-east-1:123456789012:log-group:/aws/lambda/fake:log-stream:2026/05/26/[$LATEST]xyz789fake'
	}
];

const mockEventsMap: Record<string, ILogEvent[]> = {
	'2026/05/26/[$LATEST]abc123fake': [
		{
			timestamp: Date.parse('2026-05-26T18:15:00.000Z'),
			message: 'START RequestId: 1111-2222-3333 Version: $LATEST',
			ingestionTime: Date.parse('2026-05-26T18:15:05.000Z')
		},
		{
			timestamp: Date.parse('2026-05-26T18:15:01.000Z'),
			message:
				'2026-05-26T18:15:01.234Z\tINFO\tExecuting action handler for publish project completion',
			ingestionTime: Date.parse('2026-05-26T18:15:05.000Z')
		},
		{
			timestamp: Date.parse('2026-05-26T18:15:02.000Z'),
			message:
				'2026-05-26T18:15:02.567Z\tDEBUG\tSuccessfully verified AWS infrastructure stack status: UPDATE_COMPLETE',
			ingestionTime: Date.parse('2026-05-26T18:15:05.000Z')
		},
		{
			timestamp: Date.parse('2026-05-26T18:15:05.000Z'),
			message:
				'2026-05-26T18:15:05.890Z\tINFO\tSyncing project asset metadata for project: proj_alpha',
			ingestionTime: Date.parse('2026-05-26T18:15:10.000Z')
		},
		{
			timestamp: Date.parse('2026-05-26T18:15:10.000Z'),
			message: 'END RequestId: 1111-2222-3333',
			ingestionTime: Date.parse('2026-05-26T18:15:15.000Z')
		},
		{
			timestamp: Date.parse('2026-05-26T18:15:10.000Z'),
			message:
				'REPORT RequestId: 1111-2222-3333\tDuration: 10000.12 ms\tBilled Duration: 10001 ms\tMemory Size: 1024 MB\tMax Memory Used: 96 MB\tInit Duration: 350.21 ms',
			ingestionTime: Date.parse('2026-05-26T18:15:15.000Z')
		}
	],
	'2026/05/26/[$LATEST]xyz789fake': [
		{
			timestamp: Date.parse('2026-05-26T17:15:00.000Z'),
			message: 'START RequestId: 4444-5555-6666 Version: $LATEST',
			ingestionTime: Date.parse('2026-05-26T17:15:05.000Z')
		},
		{
			timestamp: Date.parse('2026-05-26T17:15:01.000Z'),
			message:
				'2026-05-26T17:15:01.001Z\tINFO\tInitializing Stripe API client connection...',
			ingestionTime: Date.parse('2026-05-26T17:15:05.000Z')
		},
		{
			timestamp: Date.parse('2026-05-26T17:15:03.000Z'),
			message:
				'2026-05-26T17:15:03.456Z\tWARN\tStripe API key is not set, falling back to mock payment processor',
			ingestionTime: Date.parse('2026-05-26T17:15:05.000Z')
		},
		{
			timestamp: Date.parse('2026-05-26T17:15:05.000Z'),
			message: 'END RequestId: 4444-5555-6666',
			ingestionTime: Date.parse('2026-05-26T17:15:10.000Z')
		}
	]
};

const translator = short();

const getMethodColor = (method: string) => {
	switch (method.toUpperCase()) {
		case 'GET':
			return '#0ea5e9';
		case 'POST':
			return '#22c55e';
		case 'PUT':
			return '#eab308';
		case 'DELETE':
			return '#ef4444';
		default:
			return '#6b7280';
	}
};

const getFunctionNameForRoute = (route: any, endpoints: any[]) => {
	const functionIdParts =
		route.target?.resource?.resourceId?.split('-') || [];
	if (functionIdParts.length === 0) return '';

	const shortId = functionIdParts[functionIdParts.length - 1];
	let entityId = '';
	try {
		entityId = translator.toUUID(shortId);
	} catch (e) {
		entityId = shortId;
	}

	const matchingEndpoint = endpoints.find(
		(r) =>
			r.resourceId === entityId ||
			r.resourceId === route.target?.resource?.resourceId
	);

	const details = matchingEndpoint?.details as any;
	const rawFuncName =
		details?.functionName || route.target?.resource?.resourceId || '';
	return getFunctionNameFromArn(rawFuncName);
};

const CloudLogsViewer: React.FC<ICloudLogsViewerProps> = ({
	onClose,
	project,
	projectId,
	functionName: propFunctionName,
	logGroupName,
	hideHeader
}) => {
	const activeWorkspace = useWorkspaceStore((state) => state.activeWorkspace);
	const showPaywall = !(activeWorkspace?.features?.logging ?? false);

	const params = useParams<{
		functionName: string;
	}>();
	const functionName =
		propFunctionName || getFunctionNameFromArn(params.functionName || '');
	const logGroupNameQuery = logGroupName
		? `&logGroupName=${encodeURIComponent(logGroupName)}`
		: '';

	const [streams, setStreams] = useState<ILogStream[]>([]);
	const [selectedStreamName, setSelectedStreamName] = useState<string | null>(
		null
	);
	const [events, setEvents] = useState<ILogEvent[]>([]);
	const [isLoadingStreams, setIsLoadingStreams] = useState<boolean>(true);
	const [isLoadingEvents, setIsLoadingEvents] = useState<boolean>(false);

	const consoleEndRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const scrollToBottom = () => {
		if (containerRef.current) {
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
		}
	};

	const scrollToTop = () => {
		if (containerRef.current) {
			containerRef.current.scrollTop = 0;
		}
	};

	const copyToClipboard = () => {
		const text = events
			.map(
				(evt: ILogEvent) =>
					`[${formatTimestamp(evt.timestamp)}] ${evt.message}`
			)
			.join('\n');
		navigator.clipboard.writeText(text);

		popupNotification({
			color: 'success',
			text: `${events.length} log ${events.length === 1 ? 'line' : 'lines'} copied to clipboard`
		});
	};

	const headerInfo = React.useMemo(() => {
		const resources = (project?.liveResources as any[]) || [];

		// 1. Try to find a match among HTTP endpoints / routes first
		const httpApiResources = resources.filter(
			(r) => r.resourceType === InfrastructureResourceType.HttpApi
		);
		const endpoints = resources.filter(
			(r) => r.resourceType === InfrastructureResourceType.HttpEndpoint
		);
		const routes = httpApiResources.flatMap(
			(r) => (r.details as any)?.routes || []
		);

		const matchingRoute = routes.find((route: any) => {
			return getFunctionNameForRoute(route, endpoints) === functionName;
		});

		if (matchingRoute) {
			const matchingEndpoint = endpoints.find((r) => {
				return (
					(r.details as any)?.functionName === functionName ||
					r.resourceId === functionName
				);
			});
			const rawName = matchingEndpoint?.name || '';
			const readableName =
				resolveActionDescriptorNameById(rawName) ||
				toSentenceCase(rawName) ||
				'HTTP Endpoint';

			return {
				type: 'http' as const,
				method: matchingRoute.method || 'GET',
				path: matchingRoute.path || '/',
				name: readableName,
				iconFile: HTTPEndpointIcon,
				icon: 'fa-solid fa-globe'
			};
		}

		// 2. Try to find among Cron Jobs
		const cronJobs = resources.filter(
			(r) => r.resourceType === InfrastructureResourceType.CronJob
		);
		const matchingCron = cronJobs.find((r) => {
			return (
				(r.details as any)?.functionName === functionName ||
				r.resourceId === functionName
			);
		});
		if (matchingCron) {
			const rawName = matchingCron.name || '';
			const readableName =
				resolveActionDescriptorNameById(rawName) ||
				toSentenceCase(rawName) ||
				'Scheduled execution';
			return {
				type: 'cron' as const,
				name: readableName,
				schedule:
					(matchingCron.details as any)?.scheduleExpression || '',
				iconFile: CronJobIcon,
				icon: 'fa-solid fa-clock'
			};
		}

		// 3. Try to find among Serverless Functions (global events etc)
		const serverlessFunctions = resources.filter(
			(r) =>
				r.resourceType === InfrastructureResourceType.ServerlessFunction
		);
		const matchingFunc = serverlessFunctions.find((r) => {
			return (
				(r.details as any)?.functionName === functionName ||
				r.resourceId === functionName
			);
		});
		if (matchingFunc) {
			const rawName = matchingFunc.name || '';
			const readableName =
				resolveActionDescriptorNameById(rawName) ||
				toSentenceCase(rawName) ||
				'Serverless Function';
			return {
				type: 'function' as const,
				name: readableName,
				iconFile: ExecutionIcon,
				icon: 'fa-solid fa-bolt'
			};
		}

		// Fallback
		return {
			type: 'fallback' as const,
			name: functionName || 'Logs',
			iconFile: TerminalIcon,
			icon: 'fa-solid fa-terminal'
		};
	}, [project?.liveResources, functionName]);

	const fetchStreams = async () => {
		if (showPaywall) {
			setStreams(mockStreams);
			if (mockStreams.length > 0 && !selectedStreamName) {
				setSelectedStreamName(mockStreams[0].logStreamName);
			}
			setIsLoadingStreams(false);
			return;
		}
		if (!projectId || !functionName) {
			setIsLoadingStreams(false);
			return;
		}
		setIsLoadingStreams(true);
		try {
			const accessToken = await getAccessToken();
			const response = await axios.get(
				`${envConfig.API_BASE_URL}/publishing-service/v1/logs/${projectId}/streams?functionName=${functionName}${logGroupNameQuery}`,
				{
					headers: {
						Authorization: `Bearer ${accessToken}`
					}
				}
			);

			const fetchedStreams = response.data?.data?.streams || [];
			setStreams(fetchedStreams);

			if (fetchedStreams.length > 0 && !selectedStreamName) {
				setSelectedStreamName(fetchedStreams[0].logStreamName);
			}
		} catch (error) {
			console.error('Error fetching log streams:', error);
			popupNotification({
				text: 'Error loading log streams. Please try again.',
				color: 'error'
			});
		} finally {
			setIsLoadingStreams(false);
		}
	};

	const fetchEvents = async (streamName: string) => {
		if (showPaywall) {
			setEvents(mockEventsMap[streamName] || []);
			setIsLoadingEvents(false);
			return;
		}
		if (!projectId || !functionName || !streamName) return;
		setIsLoadingEvents(true);
		try {
			const accessToken = await getAccessToken();
			const response = await axios.get(
				`${envConfig.API_BASE_URL}/publishing-service/v1/logs/${projectId}/events?functionName=${functionName}&streamName=${encodeURIComponent(streamName)}${logGroupNameQuery}`,
				{
					headers: {
						Authorization: `Bearer ${accessToken}`
					}
				}
			);

			setEvents(response.data?.data?.events || []);
		} catch (error) {
			console.error('Error fetching log events:', error);
			popupNotification({
				text: 'Error loading log events. Please try again.',
				color: 'error'
			});
		} finally {
			setIsLoadingEvents(false);
		}
	};

	useEffect(() => {
		fetchStreams();
	}, [projectId, functionName]);

	useEffect(() => {
		if (selectedStreamName) {
			fetchEvents(selectedStreamName);
		} else {
			setEvents([]);
		}
	}, [selectedStreamName]);

	useEffect(() => {
		if (consoleEndRef.current) {
			consoleEndRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	}, [events, isLoadingEvents]);

	const formatTimestamp = (ts: number) => {
		const d = new Date(ts);
		return (
			d.toLocaleTimeString() +
			'.' +
			String(d.getMilliseconds()).padStart(3, '0')
		);
	};

	const getLogLevel = (message: string) => {
		const upper = message.toUpperCase();
		if (
			upper.includes('ERROR') ||
			upper.includes('EXCEPTION') ||
			upper.includes('FAIL')
		) {
			return 'ERROR';
		}
		if (upper.includes('WARN')) {
			return 'WARN';
		}
		if (upper.includes('DEBUG')) {
			return 'DEBUG';
		}
		return 'INFO';
	};

	const getLogColor = (level: string) => {
		switch (level) {
			case 'ERROR':
				return '#ff6b6b';
			case 'WARN':
				return '#ffd43b';
			case 'DEBUG':
				return '#4dabf7';
			default:
				return '#a9e34b';
		}
	};

	const handleBack = () => {
		// Go back in router history
		window.history.back();
	};

	return (
		<div
			style={{
				position: 'relative',
				height: hideHeader ? '100%' : 'calc(100% - 90px)',
				display: 'flex',
				flexDirection: 'column'
			}}
		>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					padding: hideHeader ? '0' : '24px 40px',
					fontFamily: "'Outfit', 'Inter', sans-serif",
					filter: showPaywall ? 'blur(3px)' : 'none',
					opacity: showPaywall ? 0.4 : 1,
					pointerEvents: showPaywall ? 'none' : 'auto',
					userSelect: showPaywall ? 'none' : 'auto'
				}}
			>
				{/* Top Panel */}
				{!hideHeader && (
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							marginBottom: '20px'
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '12px'
							}}
						>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									gap: '4px'
								}}
							>
								{/* Differentiated context-sensitive back button */}
								<button
									onClick={handleBack}
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '6px',
										background: 'none',
										border: 'none',
										cursor: 'pointer',
										fontSize: '12px',
										fontWeight: 500,
										color: '#6366f1',
										padding: 0,
										alignSelf: 'flex-start',
										transition: 'color 0.15s ease'
									}}
									onMouseEnter={(e) =>
										(e.currentTarget.style.color =
											'#4f46e5')
									}
									onMouseLeave={(e) =>
										(e.currentTarget.style.color =
											'#6366f1')
									}
								>
									<i
										className="fa-solid fa-chevron-left"
										style={{ fontSize: '10px' }}
									></i>
									{headerInfo.type === 'http' &&
										'Back to HTTP Endpoints'}
									{headerInfo.type === 'cron' &&
										'Back to Scheduled executions'}
									{headerInfo.type === 'function' &&
										'Back to Serverless Functions'}
									{headerInfo.type === 'fallback' &&
										'Back to resources'}
								</button>

								{/* Title area */}
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '10px',
										marginTop: '4px'
									}}
								>
									{headerInfo.iconFile ? (
										<img
											src={headerInfo.iconFile}
											alt=""
											style={{
												width: '22px',
												height: '22px',
												objectFit: 'contain'
											}}
										/>
									) : (
										<i
											className={headerInfo.icon}
											style={{
												color: '#4f46e5',
												fontSize: '16px'
											}}
										></i>
									)}

									{headerInfo.type === 'http' ? (
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '10px',
												flexWrap: 'wrap'
											}}
										>
											<span
												style={{
													display: 'inline-block',
													padding: '2px 8px',
													borderRadius: '4px',
													fontSize: '11px',
													fontWeight: 700,
													backgroundColor: `${getMethodColor(headerInfo.method)}15`,
													color: getMethodColor(
														headerInfo.method
													),
													textTransform: 'uppercase'
												}}
											>
												{headerInfo.method}
											</span>
											<code
												style={{
													fontFamily: 'monospace',
													fontSize: '14px',
													fontWeight: 600,
													color: '#333'
												}}
											>
												{headerInfo.path}
											</code>
											<span
												style={{
													color: '#9ca3af',
													fontSize: '14px'
												}}
											>
												—
											</span>
											<h2
												style={{
													margin: '0px',
													fontSize: '18px',
													fontWeight: 600,
													color: '#111827'
												}}
											>
												{headerInfo.name}
											</h2>
										</div>
									) : headerInfo.type === 'cron' ? (
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '10px',
												flexWrap: 'wrap'
											}}
										>
											<h2
												style={{
													margin: '0px',
													fontSize: '18px',
													fontWeight: 600,
													color: '#111827'
												}}
											>
												{headerInfo.name}
											</h2>
											<span
												style={{
													color: '#9ca3af',
													fontSize: '14px'
												}}
											>
												—
											</span>
											<code
												style={{
													fontFamily: 'monospace',
													fontSize: '13px',
													color: '#4b5563',
													backgroundColor: '#f3f4f6',
													padding: '2px 6px',
													borderRadius: '4px'
												}}
											>
												{headerInfo.schedule}
											</code>
										</div>
									) : (
										<h2
											style={{
												margin: '0px',
												fontSize: '18px',
												fontWeight: 600,
												color: '#111827'
											}}
										>
											{headerInfo.name}
										</h2>
									)}
								</div>

								<span
									style={{
										fontSize: '12px',
										color: '#6b7280',
										marginTop: '2px'
									}}
								>
									Log Streams
								</span>
							</div>
						</div>

						<div style={{ display: 'flex', gap: '10px' }}>
							<Button
								variant="outlined"
								size="small"
								iconStart="fa-solid fa-rotate"
								loading={isLoadingStreams || isLoadingEvents}
								onClick={() => {
									if (selectedStreamName) {
										fetchEvents(selectedStreamName);
									}
									fetchStreams();
								}}
							>
								Refresh
							</Button>
						</div>
					</div>
				)}

				{/* Main Split Layout */}
				<div
					style={{
						display: 'flex',
						flex: 1,
						gap: '20px',
						minHeight: '400px',
						overflow: 'hidden'
					}}
				>
					{/* Left Sidebar: Stream Selector */}
					<div
						style={{
							width: '260px',
							display: 'flex',
							flexDirection: 'column',
							border: '1px solid #e0e0e0',
							borderRadius: '10px',
							backgroundColor: '#fdfdfd',
							overflowY: 'auto',
							maxHeight: '550px'
						}}
					>
						<div
							style={{
								padding: '12px 16px',
								borderBottom: '1px solid #e0e0e0',
								fontWeight: 600,
								fontSize: '13px',
								color: '#444',
								backgroundColor: '#f5f5f5'
							}}
						>
							Streams{' '}
							{isLoadingStreams ? '' : `(${streams.length})`}
						</div>
						{isLoadingStreams ? (
							<div
								style={{
									padding: '20px',
									textAlign: 'center',
									color: '#666',
									fontSize: '13px'
								}}
							>
								<i
									className="fa-solid fa-spinner fa-spin"
									style={{ marginRight: '8px' }}
								></i>
								Loading streams...
							</div>
						) : streams.length === 0 ? (
							<div
								style={{
									padding: '20px',
									textAlign: 'center',
									color: '#888',
									fontSize: '13px'
								}}
							>
								No execution logs found. Run the service to
								generate logs.
							</div>
						) : (
							<div
								style={{
									display: 'flex',
									flexDirection: 'column'
								}}
							>
								{streams.map((stream) => {
									const isSelected =
										selectedStreamName ===
										stream.logStreamName;
									const eventTime =
										stream.lastEventTimestamp ||
										stream.creationTime;
									const lastTimeFormatted = eventTime
										? new Date(eventTime).toLocaleString()
										: 'Unknown';
									const isEmpty =
										(stream as any).storedBytes === 0 &&
										!stream.lastEventTimestamp;

									return (
										<button
											key={stream.logStreamName}
											onClick={() =>
												setSelectedStreamName(
													stream.logStreamName
												)
											}
											style={{
												width: '100%',
												padding: '12px 16px',
												textAlign: 'left',
												background: isSelected
													? 'rgba(99, 102, 241, 0.08)'
													: 'none',
												border: 'none',
												borderBottom:
													'1px solid #f0f0f0',
												cursor: 'pointer',
												display: 'flex',
												flexDirection: 'column',
												gap: '4px',
												transition: 'background 0.2s'
											}}
										>
											<span
												style={{
													fontWeight: isSelected
														? 600
														: 400,
													color: isSelected
														? '#4f46e5'
														: '#333',
													fontSize: '12px',
													wordBreak: 'break-all'
												}}
											>
												{stream.logStreamName.substring(
													0,
													30
												)}
												...
											</span>
											<span
												style={{
													fontSize: '10px',
													color: '#888'
												}}
											>
												{isEmpty
													? 'Created: '
													: 'Last Active: '}{' '}
												{lastTimeFormatted}
											</span>
										</button>
									);
								})}
							</div>
						)}
					</div>

					{/* Right Console: Monospace Event Viewer */}
					<div
						style={{
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
							border: '1px solid #2d2d2d',
							borderRadius: '10px',
							backgroundColor: '#0d1117',
							boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
							overflow: 'hidden'
						}}
					>
						{/* Terminal Header */}
						<div
							style={{
								backgroundColor: '#161b22',
								padding: '8px 16px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								borderBottom: '1px solid #21262d'
							}}
						>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '6px'
								}}
							>
								<div
									style={{
										width: '12px',
										height: '12px',
										borderRadius: '50%',
										backgroundColor: '#ff5f56'
									}}
								/>
								<div
									style={{
										width: '12px',
										height: '12px',
										borderRadius: '50%',
										backgroundColor: '#ffbd2e'
									}}
								/>
								<div
									style={{
										width: '12px',
										height: '12px',
										borderRadius: '50%',
										backgroundColor: '#27c93f'
									}}
								/>
								<span
									style={{
										marginLeft: '12px',
										color: '#8b949e',
										fontFamily: 'monospace',
										fontSize: '11px'
									}}
								>
									{selectedStreamName || 'Select a stream'}
								</span>
							</div>
							{selectedStreamName && (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '12px'
									}}
								>
									<button
										onClick={copyToClipboard}
										title="Copy to clipboard"
										style={{
											background: 'none',
											border: 'none',
											cursor: 'pointer',
											color: '#8b949e',
											fontSize: '12px',
											display: 'flex',
											alignItems: 'center',
											gap: '4px',
											padding: 0
										}}
										onMouseEnter={(e) =>
											(e.currentTarget.style.color =
												'#c9d1d9')
										}
										onMouseLeave={(e) =>
											(e.currentTarget.style.color =
												'#8b949e')
										}
									>
										<i className="fa-light fa-copy" /> Copy
									</button>
									<button
										onClick={scrollToTop}
										title="Scroll to top"
										style={{
											background: 'none',
											border: 'none',
											cursor: 'pointer',
											color: '#8b949e',
											fontSize: '12px',
											display: 'flex',
											alignItems: 'center',
											gap: '4px',
											padding: 0
										}}
										onMouseEnter={(e) =>
											(e.currentTarget.style.color =
												'#c9d1d9')
										}
										onMouseLeave={(e) =>
											(e.currentTarget.style.color =
												'#8b949e')
										}
									>
										<i className="fa-light fa-arrow-up" /> Top
									</button>
									<button
										onClick={scrollToBottom}
										title="Scroll to bottom"
										style={{
											background: 'none',
											border: 'none',
											cursor: 'pointer',
											color: '#8b949e',
											fontSize: '12px',
											display: 'flex',
											alignItems: 'center',
											gap: '4px',
											padding: 0
										}}
										onMouseEnter={(e) =>
											(e.currentTarget.style.color =
												'#c9d1d9')
										}
										onMouseLeave={(e) =>
											(e.currentTarget.style.color =
												'#8b949e')
										}
									>
										<i className="fa-light fa-arrow-down" />{' '}
										Bottom
									</button>
									<span
										style={{
											fontSize: '11px',
											color: '#8b949e',
											fontFamily: 'monospace'
										}}
									>
										{events.length} events
									</span>
								</div>
							)}
						</div>

						{/* Terminal Body */}
						<div
							ref={containerRef}
							style={{
								flex: 1,
								padding: '16px',
								overflowY: 'auto',
								fontFamily:
									"'Fira Code', 'Courier New', Courier, monospace",
								fontSize: '12px',
								lineHeight: '1.6',
								color: '#c9d1d9',
								display: 'flex',
								flexDirection: 'column',
								gap: '6px'
							}}
						>
							{isLoadingEvents ? (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										height: '100%',
										color: '#8b949e'
									}}
								>
									<i
										className="fa-solid fa-spinner fa-spin"
										style={{
											marginRight: '8px',
											fontSize: '16px'
										}}
									></i>
									Loading events...
								</div>
							) : !selectedStreamName ? (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										height: '100%',
										color: '#8b949e',
										textAlign: 'center'
									}}
								>
									Select an execution log stream from the left
									list to view standard output.
								</div>
							) : events.length === 0 ? (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										height: '100%',
										color: '#8b949e',
										textAlign: 'center'
									}}
								>
									No log events recorded in this stream yet.
								</div>
							) : (
								<>
									{events.map((evt, idx) => {
										const level = getLogLevel(evt.message);
										const color = getLogColor(level);

										return (
											<div
												key={idx}
												style={{
													display: 'flex',
													alignItems: 'flex-start',
													gap: '12px',
													wordBreak: 'break-all',
													whiteSpace: 'pre-wrap',
													borderBottom:
														'1px solid rgba(255,255,255,0.02)',
													paddingBottom: '4px'
												}}
											>
												{/* Pointing to line number */}
												<span
													style={{
														color: '#58a6ff',
														flexShrink: 0,
														userSelect: 'none'
													}}
												>
													[
													{formatTimestamp(
														evt.timestamp
													)}
													]
												</span>

												{/* Log Level Chip */}
												<span
													style={{
														color: color,
														fontWeight: 600,
														flexShrink: 0,
														userSelect: 'none',
														minWidth: '55px'
													}}
												>
													{level}
												</span>

												{/* Log Message */}
												<span style={{ flex: 1 }}>
													{evt.message}
												</span>
											</div>
										);
									})}
									<div ref={consoleEndRef} />
								</>
							)}
						</div>
					</div>
				</div>
			</div>

			{showPaywall && (
				<div
					style={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						zIndex: 100,
						width: '90%',
						maxWidth: '480px',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						pointerEvents: 'none',
						backgroundColor: 'transparent'
					}}
				>
					<UpgradeBanner />
				</div>
			)}
		</div>
	);
};

export default CloudLogsViewer;
